import React, { useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Checkbox,
  ButtonBase,
} from "@mui/material";
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Group,
  PaymentsOutlined,
  AssessmentOutlined,
  AttachMoneyOutlined,
} from "@mui/icons-material";
import { BRAND } from "../../components/auth/authTheme";
import MobileMonthYearFilters from "../../components/mobile/MobileMonthYearFilters";

const NAVY = BRAND.navy;
const ORANGE = BRAND.orangeHover;
const BG = "#F8FAFC";

const TabPill = ({ label, isActive, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      flex: 1,
      py: 1,
      borderRadius: "10px",
      fontWeight: 600,
      fontSize: "0.8125rem",
      color: isActive ? ORANGE : BRAND.muted,
      bgcolor: isActive ? BRAND.orangeLight : "transparent",
      border: `1px solid ${isActive ? ORANGE : BRAND.border}`,
      transition: "all 0.15s",
    }}
  >
    {label}
  </ButtonBase>
);

const StatCard = ({ icon, label, value, iconBg, iconColor }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: "12px",
      px: 0.75,
      py: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: 0.5,
      border: `1px solid ${BRAND.border}`,
      height: "100%",
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: "8px",
        bgcolor: iconBg,
        color: iconColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </Box>
    <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: NAVY, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.625rem",
        color: BRAND.muted,
        fontWeight: 500,
        lineHeight: 1.25,
        whiteSpace: "normal",
        wordBreak: "break-word",
        width: "100%",
      }}
    >
      {label}
    </Typography>
  </Box>
);

const EmployeePayrollCard = ({
  employee,
  isSelected,
  onToggleSelect,
  onView,
  presentDaysLabel,
}) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: "12px",
      mb: 1.25,
      border: isSelected ? `2px solid ${ORANGE}` : `1px solid ${BRAND.border}`,
      boxShadow: "0 1px 6px rgba(26, 43, 74, 0.04)",
      overflow: "hidden",
    }}
  >
    <Box sx={{ px: 1.25, py: 0.875 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "24px 1fr 32px",
          alignItems: "center",
          columnGap: 0.75,
          mb: 0.35,
        }}
      >
        <Checkbox
          size="small"
          checked={isSelected}
          onChange={() => onToggleSelect(employee)}
          sx={{
            p: 0,
            color: BRAND.muted,
            "&.Mui-checked": { color: ORANGE },
          }}
          inputProps={{ "aria-label": `Select ${employee.employee_name}` }}
        />
        <Typography
          sx={{
            fontWeight: 700,
            color: NAVY,
            fontSize: "0.875rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {employee.employee_name || "Unknown"}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onView(employee)}
          aria-label="View employee payroll"
          sx={{
            p: 0,
            color: "#2196f3",
            bgcolor: "rgba(33, 150, 243, 0.08)",
            width: 32,
            height: 32,
          }}
        >
          <VisibilityIcon sx={{ fontSize: "1.05rem" }} />
        </IconButton>
      </Box>
      <Typography
        sx={{ fontSize: "0.6875rem", fontWeight: 600, color: ORANGE, pl: "calc(24px + 6px)", mb: 0.5 }}
      >
        {employee.emp_id || "N/A"}
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          pl: "calc(24px + 6px)",
          flexWrap: "wrap",
        }}
      >
        <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted }}>
          Basic: <strong style={{ color: NAVY }}>{employee.basic_salary ?? "N/A"}</strong>
        </Typography>
        <Typography sx={{ fontSize: "0.6875rem", color: BRAND.border }}>
          ·
        </Typography>
        <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted }}>
          {presentDaysLabel}:{" "}
          <strong style={{ color: NAVY }}>
            {employee.present_days_last_month ?? "N/A"}
          </strong>
        </Typography>
      </Box>
    </Box>
  </Box>
);

const PayslipCard = ({ record, onView, onDelete }) => {
  const monthLabel = record.month
    ? new Date(record.month).toLocaleString("default", {
        month: "short",
        year: "numeric",
      })
    : "N/A";

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "12px",
        mb: 1.25,
        border: `1px solid ${BRAND.border}`,
        boxShadow: "0 1px 6px rgba(26, 43, 74, 0.04)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 1.25, py: 0.875 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr auto auto",
            alignItems: "center",
            columnGap: 0.5,
            mb: 0.35,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: NAVY,
              fontSize: "0.875rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {record.employee_name || "Unknown"}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onView(record)}
            sx={{ p: 0, color: "#2196f3", width: 32, height: 32 }}
          >
            <VisibilityIcon sx={{ fontSize: "1.05rem" }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onDelete(record.id)}
            sx={{ p: 0, color: "#d32f2f", width: 32, height: 32 }}
          >
            <DeleteIcon sx={{ fontSize: "1.05rem" }} />
          </IconButton>
        </Box>
        <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: ORANGE, mb: 0.5 }}>
          {monthLabel}
        </Typography>
        <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted }}>
          Net salary:{" "}
          <strong style={{ color: NAVY }}>
            {record.salary?.net_salary != null
              ? Number(record.salary.net_salary).toFixed(2)
              : "N/A"}
          </strong>
        </Typography>
      </Box>
    </Box>
  );
};

const PayrollMobileView = ({
  activeTab,
  setActiveTab,
  employees,
  payslips,
  searchTerm,
  setSearchTerm,
  selectedRows,
  onToggleSelect,
  onEmployeeView,
  onPayslipView,
  onPayslipDelete,
  selectedMonth,
  setSelectedMonth,
  selectedYear,
  setSelectedYear,
  yearOptions,
  onAddPayroll,
  onGenerateReport,
  canCreate,
  canGenerateReport,
  presentDaysLabel,
}) => {
  const selectedIds = useMemo(
    () =>
      new Set(
        selectedRows.map((row) => String(row.employee_id || row.id))
      ),
    [selectedRows]
  );

  const employeeStats = useMemo(
    () => ({
      total: employees.length,
      selected: selectedRows.length,
    }),
    [employees, selectedRows]
  );

  const payslipStats = useMemo(
    () => ({
      total: payslips.length,
      netTotal: payslips.reduce(
        (sum, p) => sum + Number(p.salary?.net_salary || 0),
        0
      ),
    }),
    [payslips]
  );

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: String(i + 1),
        label: new Date(0, i).toLocaleString("default", { month: "short" }),
      })),
    []
  );

  return (
    <Box sx={{ pb: 10, pt: 1, px: 2, bgcolor: BG, minHeight: "100vh" }}>
      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: NAVY, mb: 0.25 }}>
        Payroll Processing
      </Typography>
      <Typography sx={{ fontSize: "0.8125rem", color: BRAND.muted, mb: 1.5 }}>
        {activeTab === 0
          ? "Select employees and generate monthly payroll"
          : "Review generated payslips"}
      </Typography>

      <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
        <TabPill
          label="Employees"
          isActive={activeTab === 0}
          onClick={() => setActiveTab(0)}
        />
        <TabPill
          label="Payroll List"
          isActive={activeTab === 1}
          onClick={() => setActiveTab(1)}
        />
      </Box>

      {activeTab === 0 ? (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1, mb: 2 }}>
          <StatCard
            icon={<Group sx={{ fontSize: "1rem" }} />}
            label="Employees"
            value={employeeStats.total}
            iconBg="rgba(33,150,243,0.15)"
            iconColor="#2196f3"
          />
          <StatCard
            icon={<PaymentsOutlined sx={{ fontSize: "1rem" }} />}
            label="Selected"
            value={employeeStats.selected}
            iconBg="rgba(230,90,0,0.12)"
            iconColor={ORANGE}
          />
        </Box>
      ) : (
        <>
          <MobileMonthYearFilters
            sx={{ mb: 1.5 }}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            onMonthChange={setSelectedMonth}
            onYearChange={setSelectedYear}
            monthOptions={monthOptions}
            yearOptions={yearOptions}
            allowAllMonths
            useShortMonthLabel
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1, mb: 2 }}>
            <StatCard
              icon={<AssessmentOutlined sx={{ fontSize: "1rem" }} />}
              label="Payslips"
              value={payslipStats.total}
              iconBg="rgba(76,175,80,0.15)"
              iconColor="#4caf50"
            />
            <StatCard
              icon={<AttachMoneyOutlined sx={{ fontSize: "1rem" }} />}
              label="Net Total"
              value={payslipStats.netTotal.toFixed(0)}
              iconBg="rgba(255,152,0,0.15)"
              iconColor="#ff9800"
            />
          </Box>
          {canGenerateReport && (
            <Box
              component="button"
              type="button"
              onClick={onGenerateReport}
              sx={{
                width: "100%",
                mb: 2,
                py: 1.1,
                border: "none",
                borderRadius: "12px",
                bgcolor: ORANGE,
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.875rem",
                cursor: "pointer",
                boxShadow: "0 2px 10px rgba(230, 90, 0, 0.25)",
              }}
            >
              Generate Salary Report
            </Box>
          )}
        </>
      )}

      {activeTab === 0 && selectedRows.length > 0 && canCreate && (
        <Box
          sx={{
            mb: 1.5,
            px: 1.5,
            py: 1,
            bgcolor: BRAND.orangeLight,
            borderRadius: "10px",
            border: `1px solid ${ORANGE}33`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: NAVY }}>
            {selectedRows.length} selected
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={onAddPayroll}
            sx={{
              border: "none",
              borderRadius: "8px",
              bgcolor: ORANGE,
              color: "#fff",
              fontWeight: 600,
              fontSize: "0.75rem",
              px: 1.5,
              py: 0.75,
              cursor: "pointer",
            }}
          >
            Add Payroll
          </Box>
        </Box>
      )}

      <TextField
        fullWidth
        placeholder={
          activeTab === 0 ? "Search employees..." : "Search payrolls..."
        }
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            bgcolor: "#fff",
            borderRadius: "12px",
            "& fieldset": { borderColor: BRAND.border },
            "&.Mui-focused fieldset": { borderColor: ORANGE },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: BRAND.muted }} />
            </InputAdornment>
          ),
        }}
      />

      {activeTab === 0 ? (
        employees.length > 0 ? (
          employees.map((emp) => (
            <EmployeePayrollCard
              key={emp.employee_id || emp.id}
              employee={emp}
              isSelected={selectedIds.has(String(emp.employee_id || emp.id))}
              onToggleSelect={onToggleSelect}
              onView={onEmployeeView}
              presentDaysLabel={presentDaysLabel}
            />
          ))
        ) : (
          <EmptyState
            message="No employees found"
            hint="Employees with salary details will appear here."
          />
        )
      ) : payslips.length > 0 ? (
        payslips.map((record) => (
          <PayslipCard
            key={record.id}
            record={record}
            onView={onPayslipView}
            onDelete={onPayslipDelete}
          />
        ))
      ) : (
        <EmptyState
          message="No payroll records found"
          hint="Generate payroll from the Employees tab."
        />
      )}
    </Box>
  );
};

const EmptyState = ({ message, hint }) => (
  <Box
    sx={{
      textAlign: "center",
      py: 6,
      px: 2,
      bgcolor: "#fff",
      borderRadius: "16px",
      border: `1px solid ${BRAND.border}`,
    }}
  >
    <PaymentsOutlined sx={{ fontSize: 48, color: "#CBD5E1", mb: 1 }} />
    <Typography sx={{ fontWeight: 600, color: NAVY, mb: 0.5 }}>
      {message}
    </Typography>
    <Typography sx={{ fontSize: "0.875rem", color: BRAND.muted }}>
      {hint}
    </Typography>
  </Box>
);

export default PayrollMobileView;
